import { useCallback, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { useDevices as useDailyDevices } from "@daily-co/daily-react";
import unescape from "lodash/unescape";
import { useAppDispatch } from "store/hooks";
import {
  detectMobile,
  enumerateDevices,
  getDeviceInfo,
  selectIsMobile,
  setDevicePermissions,
} from "modules/deviceInfo";
import { TRACKING_CONTEXT } from "modules/tracking";
import {
  selectUser,
  selectUserIsSyncStatus,
} from "modules/auth/redux/selectors";
import TimezoneUpdate from "modules/userProfile/hooks/TimezoneUpdate/TimezoneUpdate";
import {
  subscribeToGlobalUserPreference,
  unsubscribeToGlobalUserPreference,
} from "modules/globalUserPreference";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { getDirectLinkToTable } from "modules/queryParams/queryParamsHelper";
import { updateTableLink } from "modules/conversation/hooks/useCopyTableLink/slice";
import { selectCompanies } from "modules/company/redux/selectors";
import { traitType } from "modules/tracking/segment/types";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { getPermissionLevel } from "modules/audioVideo/utils";
import moment from "moment";
import { ServerTime } from "modules/serverTime/ServerTime";
import { VIRTUAL_BACKGROUND_CACHE_KEY } from "modules/audioVideo/hooks/useVirtualBackground/constants";
import { setCurrentBackground } from "modules/audioVideo/redux/slice";
import { useVirtualBackground } from "modules/audioVideo/hooks/useVirtualBackground";
import AuthVerify from "./AuthVerify";

const NORMALIZE_SEARCH_PARAMS_AT_PATHS = ["/reset-password"];

const BackgroundInitialization = () => {
  const user = useSelector(selectUser);
  const isTokenSyncedWithServer = useSelector(selectUserIsSyncStatus);
  const { myCompanyPlan } = useCompanyPlanSettings();
  const { camState, micState } = useDailyDevices();
  const { hasDevicesAPI } = useSelector(getDeviceInfo);
  const userCompanies = useSelector(selectCompanies);
  const { setVirtualBackground } = useVirtualBackground();
  const { forceDesktop, forceMobile, ...params } = useQueryParams();
  const virtualBackground = localStorage.getItem(VIRTUAL_BACKGROUND_CACHE_KEY);

  const { identify } = useContext(TRACKING_CONTEXT);
  const dispatch = useAppDispatch();

  const isMobileView = useSelector(selectIsMobile);

  const fetchDevices = useCallback(() => {
    dispatch(enumerateDevices());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isTokenSyncedWithServer && user) {
      subscribeToGlobalUserPreference();
    }
  }, [isTokenSyncedWithServer, user]);

  useEffect(() => {
    if (!user) {
      unsubscribeToGlobalUserPreference();
    }

    return () => {
      unsubscribeToGlobalUserPreference();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) {
      // Tracking set identity
      const traits: traitType = {
        ...user.profile,
        country: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (userCompanies.length > 0) {
        traits.companyIds = userCompanies
          .map((userCompany) => userCompany.company.id)
          .join(", ");
      }

      if (myCompanyPlan?.trialStartedAt) {
        traits.trialStartedAt = moment(myCompanyPlan?.trialStartedAt).format(
          "YYYY-MM-DD",
        );
      }

      identify(user.email, traits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userCompanies, myCompanyPlan]);

  useEffect(() => {
    // Track when user moving away from remo
    window.addEventListener("beforeunload", () => {
      localStorage.removeItem("pushNotificationToken");
    });
  }, []);

  useEffect(() => {
    if (virtualBackground) {
      const virtualBackgroundObject = JSON.parse(virtualBackground);
      dispatch(setCurrentBackground(virtualBackgroundObject));
      setVirtualBackground(virtualBackgroundObject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (!hasDevicesAPI || isMobileView) {
      return;
    }

    navigator.mediaDevices.addEventListener("devicechange", fetchDevices);

    // eslint-disable-next-line consistent-return
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", fetchDevices);
    };
  }, [hasDevicesAPI, isMobileView, fetchDevices]);

  useEffect(() => {
    dispatch(
      detectMobile({
        forceDesktop: forceDesktop as string,
        forceMobile: forceMobile as string,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasDevicesAPI) {
      fetchDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      window.location.search &&
      NORMALIZE_SEARCH_PARAMS_AT_PATHS.includes(window.location.pathname)
    ) {
      const unescapedSearch = unescape(window.location.search);

      if (unescapedSearch !== window.location.search) {
        // Visitors from external sources may have HTML entities encoded in URL
        window.location.search = unescapedSearch;
      }
    }
  }, []);

  useEffect(() => {
    const { tableId, floorId } = getDirectLinkToTable(params);
    if (tableId && floorId) {
      dispatch(updateTableLink({ tableId, floorId }));
    }
  }, [dispatch, params]);

  useEffect(() => {
    dispatch(
      setDevicePermissions({
        audio: getPermissionLevel(micState),
        video: getPermissionLevel(camState),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camState, micState]);

  return (
    <>
      <AuthVerify />
      <TimezoneUpdate />
      <ServerTime />
    </>
  );
};

export default BackgroundInitialization;
